/**
 * Palette outils stylet — flottante en bas-droite du viewer en mode audience.
 *
 * Tactile-first : boutons 44×44 px minimum (recommandation Apple HIG +
 * Material), grappes courtes pour rester sur 2-3 rangées max.
 *
 * Pas de modal : tap sur un outil = sélection immédiate. Couleur active
 * = highlight visuel sur le swatch. Bouton « Effacer page » avec confirm
 * inline (double-tap pour confirmer).
 */
import { useState } from 'react';
import { STROKE_PRESET_COLORS, STROKE_PRESET_WIDTHS, type StrokeTool } from '../lib/strokes';

type Props = {
  active: boolean;
  onToggleActive: () => void;
  tool: StrokeTool | 'eraser';
  onToolChange: (t: StrokeTool | 'eraser') => void;
  color: string;
  onColorChange: (c: string) => void;
  width: number;
  onWidthChange: (w: number) => void;
  forceDraw: boolean;
  onForceDrawToggle: () => void;
  pendingCount: number;
  onResetPage: () => void;
  onUndoLast: () => void;
  canUndo: boolean;
};

export default function StylusPalette({
  active,
  onToggleActive,
  tool,
  onToolChange,
  color,
  onColorChange,
  width,
  onWidthChange,
  forceDraw,
  onForceDrawToggle,
  pendingCount,
  onResetPage,
  onUndoLast,
  canUndo,
}: Props) {
  const [confirmReset, setConfirmReset] = useState(false);

  if (!active) {
    return (
      <div className="pointer-events-none absolute bottom-24 right-3 z-10 flex flex-col items-end gap-2">
        <button
          type="button"
          onClick={onToggleActive}
          className="pointer-events-auto flex h-12 w-12 items-center justify-center rounded-full border border-rule bg-paper-elevated text-[22px] shadow hover:bg-paper-card"
          title="Activer le stylet"
          aria-label="Activer le stylet"
        >
          ✏️
        </button>
        {pendingCount > 0 && (
          <span className="pointer-events-auto rounded-pill bg-cat-imp px-2 py-0.5 text-[10px] font-medium text-white shadow">
            {pendingCount} en attente
          </span>
        )}
      </div>
    );
  }

  return (
    <div className="pointer-events-none absolute bottom-24 right-3 z-10 flex flex-col items-end gap-2">
      <div className="pointer-events-auto flex flex-col items-stretch gap-2 rounded-lg border border-rule bg-paper-elevated p-2 shadow-lg">
        {/* Outils */}
        <div className="flex items-center gap-1">
          <ToolButton label="Stylo" active={tool === 'pen'} onClick={() => onToolChange('pen')}>
            ✒️
          </ToolButton>
          <ToolButton
            label="Surligneur"
            active={tool === 'highlighter'}
            onClick={() => onToolChange('highlighter')}
          >
            🖍️
          </ToolButton>
          <ToolButton
            label="Gomme"
            active={tool === 'eraser'}
            onClick={() => onToolChange('eraser')}
          >
            🩹
          </ToolButton>
        </div>

        {/* Couleurs */}
        <div className="flex items-center gap-1">
          {STROKE_PRESET_COLORS.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => onColorChange(c)}
              className={`h-9 w-9 rounded-full border-2 ${
                color === c ? 'border-text-primary ring-2 ring-text-primary/30' : 'border-rule'
              }`}
              style={{ backgroundColor: c }}
              title={c}
              aria-label={`Couleur ${c}`}
            />
          ))}
        </div>

        {/* Épaisseur */}
        <div className="flex items-center gap-1">
          {STROKE_PRESET_WIDTHS.map((w) => (
            <button
              key={w}
              type="button"
              onClick={() => onWidthChange(w)}
              className={`flex h-9 w-9 items-center justify-center rounded-md border ${
                width === w ? 'border-text-primary bg-paper-card' : 'border-rule bg-paper-elevated'
              }`}
              title={`Épaisseur ${w}px`}
              aria-label={`Épaisseur ${w} pixels`}
            >
              <span
                className="block rounded-full bg-text-primary"
                style={{ width: w + 2, height: w + 2 }}
              />
            </button>
          ))}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={onUndoLast}
            disabled={!canUndo}
            className="flex h-9 flex-1 items-center justify-center rounded-md border border-rule bg-paper-elevated px-2 text-[11px] font-medium text-text-primary disabled:opacity-40"
            title="Annuler dernier trait"
            aria-label="Annuler dernier trait"
          >
            ↶ Undo
          </button>
          <button
            type="button"
            onClick={() => {
              if (!confirmReset) {
                setConfirmReset(true);
                setTimeout(() => setConfirmReset(false), 3000);
              } else {
                onResetPage();
                setConfirmReset(false);
              }
            }}
            className={`flex h-9 flex-1 items-center justify-center rounded-md border px-2 text-[11px] font-medium ${
              confirmReset
                ? 'border-cat-def bg-cat-def text-white'
                : 'border-rule bg-paper-elevated text-text-primary'
            }`}
            title="Effacer toute la page (double-tap pour confirmer)"
            aria-label="Effacer la page"
          >
            {confirmReset ? '⚠ Confirmer' : '🗑 Effacer page'}
          </button>
        </div>

        {/* Toggle force-draw doigt */}
        <label className="flex cursor-pointer items-center gap-2 text-[11px] text-text-secondary">
          <input
            type="checkbox"
            checked={forceDraw}
            onChange={onForceDrawToggle}
            className="h-4 w-4"
          />
          Dessiner au doigt
        </label>

        {/* Pending sync indicator */}
        {pendingCount > 0 && (
          <div className="rounded bg-cat-ver-pastel px-2 py-1 text-center text-[10px] font-mono text-cat-ver">
            ⏳ {pendingCount} en sync
          </div>
        )}
      </div>

      {/* Close palette */}
      <button
        type="button"
        onClick={onToggleActive}
        className="pointer-events-auto flex h-10 w-10 items-center justify-center rounded-full border border-rule bg-paper-elevated text-[14px] shadow hover:bg-paper-card"
        title="Fermer la palette stylet"
        aria-label="Fermer la palette stylet"
      >
        ✕
      </button>
    </div>
  );
}

function ToolButton({
  active,
  onClick,
  label,
  children,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex h-11 w-11 items-center justify-center rounded-md border text-[20px] ${
        active
          ? 'border-text-primary bg-paper-card ring-2 ring-text-primary/30'
          : 'border-rule bg-paper-elevated'
      }`}
      title={label}
      aria-label={label}
      aria-pressed={active}
    >
      {children}
    </button>
  );
}
